import React, { FormEvent, useMemo, useState } from 'react';
import { Activity, KeyRound, Loader2, Mic2, PhoneCall, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  findAdminQLanguageUserByEmail,
  getAdminQLanguageEntitlementUsage,
  registerAdminQLanguageInternalTester,
  removeAdminQLanguageInternalTester,
  resolveAdminQLanguageEntitlementUserId,
  type AdminQLanguageEntitlementUsage,
  type QLanguageUsageValue,
} from '../qLanguage/adminQLanguageApi';
import {
  findQLanguagePlanDefinition,
  findQLanguagePlanDefinitionByPlanCode,
  Q_LANGUAGE_APP_KEY,
  Q_LANGUAGE_PLAN_DEFINITIONS,
  Q_LANGUAGE_PRODUCT_ID,
} from '../qLanguage/qLanguageOperations';
import { formatAdminAccountDateTime } from '../types/account';
import type { AdminAccountUser } from '../types/account';
import {
  formatSubscriptionStatus,
  getStatusBadgeVariant,
} from './accounts/paymentStatusLabels';

type LookupState = {
  loading: boolean;
  error: string | null;
  queriedEmail: string | null;
  queriedUserId: string | null;
};

export const getQLanguageInternalTesterDisplay = (
  state: Pick<AdminQLanguageEntitlementUsage, 'internalTesterRegistered' | 'internalTesterSubscriptionEnabled'>,
) => ({
  registrationLabel: state.internalTesterRegistered ? '내부 테스터 등록됨' : '내부 테스터 미등록',
  subscriptionModeLabel: `앱 구독 모드: ${state.internalTesterSubscriptionEnabled ? 'Plus' : '무료'}`,
});

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return 'Q Language 구독 정보를 불러오지 못했습니다.';
};

const formatUsageValue = (value: number): string => new Intl.NumberFormat('ko-KR').format(value);

const UsageMetric = ({
  icon: Icon,
  label,
  unit,
  value,
}: {
  icon: typeof Activity;
  label: string;
  unit: string;
  value: QLanguageUsageValue;
}) => (
  <div className="rounded-lg border bg-muted/20 p-4">
    <div className="flex items-center gap-2 text-sm font-semibold">
      <Icon className="h-4 w-4" />
      {label}
    </div>
    <div className="mt-3 text-2xl font-semibold">
      {formatUsageValue(value.used)}
      <span className="ml-1 text-base font-normal text-muted-foreground">
        / {value.limit === null ? '정책 미설정' : formatUsageValue(value.limit)}
      </span>
    </div>
    <p className="mt-1 text-xs text-muted-foreground">단위: {unit}</p>
    <Badge variant="outline" className="mt-3">
      {value.remaining === null ? '잔여 한도 정책 미설정' : `잔여 ${formatUsageValue(value.remaining)}${unit}`}
    </Badge>
  </div>
);

const UnconnectedUsageMetric = ({
  icon: Icon,
  label,
}: {
  icon: typeof Activity;
  label: string;
}) => (
  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
    <div className="flex items-center gap-2 text-sm font-semibold">
      <Icon className="h-4 w-4" />
      {label}
    </div>
    <div className="mt-3 text-lg font-semibold">집계 미연결</div>
    <p className="mt-2 text-xs text-muted-foreground">
      WebRTC 세션의 실제 통화 시간 차감 경로가 연결되기 전에는 서버의 수치를 운영 지표로 사용하지 않습니다.
    </p>
  </div>
);

export default function QLanguageOperationsPage() {
  const [testerEmailInput, setTesterEmailInput] = useState('');
  const [resolvedUser, setResolvedUser] = useState<AdminAccountUser | null>(null);
  const [entitlementUsage, setEntitlementUsage] = useState<AdminQLanguageEntitlementUsage | null>(null);
  const [lookupState, setLookupState] = useState<LookupState>({
    loading: false,
    error: null,
    queriedEmail: null,
    queriedUserId: null,
  });
  const [registrationActionState, setRegistrationActionState] = useState({ loading: false, error: null as string | null });

  const plan = useMemo(
    () => {
      if (entitlementUsage?.basePlanId) {
        return findQLanguagePlanDefinition(entitlementUsage.basePlanId);
      }
      if (entitlementUsage?.planCode) {
        return findQLanguagePlanDefinitionByPlanCode(entitlementUsage.planCode);
      }
      return null;
    },
    [entitlementUsage],
  );
  const entitlementAccess = entitlementUsage ? (entitlementUsage.entitled ? 'AVAILABLE' : 'UNAVAILABLE') : 'UNKNOWN';
  const internalTesterDisplay = entitlementUsage
    ? getQLanguageInternalTesterDisplay(entitlementUsage)
    : null;

  const loadEntitlement = async (user: AdminAccountUser, email: string) => {
    const userId = resolveAdminQLanguageEntitlementUserId(user);
    setLookupState({ loading: true, error: null, queriedEmail: email, queriedUserId: userId });
    setEntitlementUsage(null);

    try {
      const result = await getAdminQLanguageEntitlementUsage(userId);
      if (result.userId !== userId) {
        throw new Error(`요청 사용자와 응답 사용자가 일치하지 않습니다: ${result.userId}`);
      }
      setEntitlementUsage(result);
      setLookupState({ loading: false, error: null, queriedEmail: email, queriedUserId: userId });
    } catch (error) {
      setLookupState({ loading: false, error: getErrorMessage(error), queriedEmail: email, queriedUserId: userId });
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = testerEmailInput.trim();
    if (!email) {
      setResolvedUser(null);
      setEntitlementUsage(null);
      setLookupState({ loading: false, error: '내부 테스터 이메일을 입력해주세요.', queriedEmail: null, queriedUserId: null });
      return;
    }

    setResolvedUser(null);
    setEntitlementUsage(null);
    setLookupState({ loading: true, error: null, queriedEmail: email, queriedUserId: null });
    try {
      const user = await findAdminQLanguageUserByEmail(email);
      setResolvedUser(user);
      await loadEntitlement(user, email);
    } catch (error) {
      setLookupState({ loading: false, error: getErrorMessage(error), queriedEmail: email, queriedUserId: null });
    }
  };

  const changeInternalTesterRegistration = async (shouldRegister: boolean) => {
    const userId = lookupState.queriedUserId;
    if (!userId) return;
    const actionConfirmed = window.confirm(shouldRegister
      ? '이 계정을 Q Language 내부 테스터로 등록할까요?'
      : '이 계정의 Q Language 내부 테스터 등록을 해제할까요?');
    if (!actionConfirmed) return;

    setRegistrationActionState({ loading: true, error: null });
    try {
      if (shouldRegister) {
        await registerAdminQLanguageInternalTester(userId, {
          reason: 'Q Language internal tester registered by operator',
        });
      } else {
        await removeAdminQLanguageInternalTester(userId);
      }
      if (!resolvedUser || !lookupState.queriedEmail) {
        throw new Error('조회한 내부 테스터 정보가 없습니다.');
      }
      await loadEntitlement(resolvedUser, lookupState.queriedEmail);
      setRegistrationActionState({ loading: false, error: null });
    } catch (error) {
      setRegistrationActionState({ loading: false, error: getErrorMessage(error) });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Q Language 내부테스터·구독 운영</h1>
            <Badge variant="outline">{Q_LANGUAGE_APP_KEY}</Badge>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            내부 테스터를 등록하고 앱에서 선택한 테스트 구독 모드와 사용량을 확인합니다.
          </p>
        </div>
        <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm">
          <div className="text-xs text-muted-foreground">Play subscription product</div>
          <div className="mt-1 font-mono font-semibold">{Q_LANGUAGE_PRODUCT_ID}</div>
        </div>
      </div>

      <section className="grid gap-4 lg:grid-cols-2">
        {Q_LANGUAGE_PLAN_DEFINITIONS.map((plan) => (
          <Card key={plan.productPlanCode}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">{plan.label}</CardTitle>
                  <CardDescription className="mt-1">Google Play base plan</CardDescription>
                </div>
                <Badge variant="secondary">{plan.billingCycle === 'MONTHLY' ? '월간' : '연간'}</Badge>
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <div className="text-xs text-muted-foreground">pay plan code</div>
                <div className="mt-1 break-all font-mono">{plan.productPlanCode}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Play base plan ID</div>
                <div className="mt-1 font-mono">{plan.basePlanId}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <Card>
        <CardHeader>
          <CardTitle>내부 테스터 등록</CardTitle>
          <CardDescription>
            Google 이메일로 계정을 찾습니다. 등록된 계정에만 앱 설정의 테스트 구독 토글이 보이며,
            등록 자체로 Plus가 활성화되지 않고 자동 만료되지 않습니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
            <Input
              aria-label="내부 테스터 Google 이메일"
              type="email"
              placeholder="내부 테스터 Google 이메일"
              value={testerEmailInput}
              onChange={(event) => setTesterEmailInput(event.target.value)}
              disabled={lookupState.loading}
            />
            <Button type="submit" disabled={lookupState.loading} className="sm:min-w-28">
              {lookupState.loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              조회
            </Button>
            {lookupState.queriedUserId ? (
              <Button
                type="button"
                variant="outline"
                disabled={lookupState.loading}
                onClick={() => resolvedUser && lookupState.queriedEmail
                  ? void loadEntitlement(resolvedUser, lookupState.queriedEmail)
                  : undefined}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                새로고침
              </Button>
            ) : null}
          </form>

          {lookupState.error ? (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {lookupState.error}
            </div>
          ) : null}

          {!lookupState.loading && lookupState.queriedUserId && !lookupState.error ? (
            <div className="mt-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/20 px-4 py-3">
                <div>
                  <div className="text-xs text-muted-foreground">내부 테스터 계정</div>
                  <div className="mt-1 break-all text-sm font-medium">{resolvedUser?.email}</div>
                  <div className="mt-1 break-all font-mono text-xs text-muted-foreground">
                    principal: {lookupState.queriedUserId}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                  <Badge variant={entitlementAccess === 'AVAILABLE' ? 'success' : entitlementAccess === 'UNAVAILABLE' ? 'destructive' : 'outline'}>
                    {entitlementAccess === 'AVAILABLE'
                      ? 'Plus 권한 활성'
                      : entitlementAccess === 'UNAVAILABLE'
                        ? 'Plus 권한 비활성'
                        : 'Plus 권한 확인 필요'}
                  </Badge>
                </div>
              </div>

              {entitlementUsage ? (
                <>
                  <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4">
                    <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
                      <div>
                        <div className="flex items-center gap-2 font-semibold">
                          <KeyRound className="h-4 w-4" />
                          내부 테스터 앱 설정
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          관리자는 내부 테스터 등록만 관리합니다. 구독 모드는 등록된 사용자가 앱 설정에서 직접 선택합니다.
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <Badge variant={entitlementUsage.internalTesterRegistered ? 'success' : 'secondary'}>
                            {internalTesterDisplay?.registrationLabel}
                          </Badge>
                          <Badge variant={entitlementUsage.internalTesterSubscriptionEnabled ? 'success' : 'outline'}>
                            {internalTesterDisplay?.subscriptionModeLabel}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                          type="button"
                          variant={entitlementUsage.internalTesterRegistered ? 'destructive' : 'default'}
                          disabled={registrationActionState.loading || lookupState.loading}
                          onClick={() => void changeInternalTesterRegistration(!entitlementUsage.internalTesterRegistered)}
                        >
                          {registrationActionState.loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                          {entitlementUsage.internalTesterRegistered ? '등록 해제' : '내부 테스터 등록'}
                        </Button>
                      </div>
                    </div>
                    {registrationActionState.error ? (
                      <div className="mt-3 text-sm text-destructive">{registrationActionState.error}</div>
                    ) : null}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>상품 / 요금제</TableHead>
                        <TableHead>권한 상태</TableHead>
                        <TableHead>자동 갱신</TableHead>
                        <TableHead>Play 구독 종료일</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <div className="font-medium">{plan?.label ?? 'Q Language 요금제 미가입'}</div>
                          <div className="mt-1 break-all font-mono text-xs text-muted-foreground">
                            {entitlementUsage.productId ?? 'product 없음'}
                            {entitlementUsage.basePlanId ? ` · ${entitlementUsage.basePlanId}` : ''}
                            {entitlementUsage.planCode ? ` · ${entitlementUsage.planCode}` : ''}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={getStatusBadgeVariant(entitlementUsage.entitlementStatus, {
                              context: 'summary',
                            })}>
                              {formatSubscriptionStatus(entitlementUsage.entitlementStatus)}
                            </Badge>
                            <span className="font-mono text-xs text-muted-foreground">
                              {entitlementUsage.entitlementStatus}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={entitlementUsage.autoRenewing ? 'success' : 'secondary'}>
                            {entitlementUsage.autoRenewing ? '갱신 예정' : '갱신 안 함'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {entitlementUsage.entitlementSource === 'STORE_PURCHASE'
                            ? formatAdminAccountDateTime(entitlementUsage.accessUntil)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                  이 사용자의 {Q_LANGUAGE_APP_KEY} entitlement 응답이 없습니다.
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>AI·음성 사용량 및 한도</CardTitle>
          <CardDescription>
            pay 서버가 집계한 현재 사용량을 표시합니다. 실제 차감 경로가 연결된 항목만 운영 지표로 사용합니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {entitlementUsage ? (
            <>
              <div className="mb-4 text-xs text-muted-foreground">
                집계 기간: {formatAdminAccountDateTime(entitlementUsage.usagePeriod.startsAt)}
                {' ~ '}
                {formatAdminAccountDateTime(entitlementUsage.usagePeriod.endsAt)}
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <UsageMetric icon={Activity} label="AI 발화 분석" unit="회" value={entitlementUsage.aiFeedback} />
                <UsageMetric icon={Mic2} label="ASR 음성 인식" unit="초" value={entitlementUsage.asrSeconds} />
                <UnconnectedUsageMetric icon={PhoneCall} label="1:1 통화" />
              </div>
            </>
          ) : (
            <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              내부 테스터 이메일을 조회하면 AI·ASR 사용량과 한도를 표시하고, 1:1 통화 집계 연결 상태를 확인합니다.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
